import React, { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  Tabs,
  Tab,
  Box,
  Grid,
  Select,
  MenuItem,
  Button,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import ShiftRosterTable from "./ShiftRosterTable";
import { Form, Formik } from "formik";
import dayjs from "dayjs";
import CardContent from "@/pages/attendanceManagement/components/CardContent";
import { useGetPayrollLockDateQuery } from "@/api/services/attendance/calendar/Calendar";
import { format, eachDayOfInterval, isValid, parseISO, startOfMonth, endOfMonth, parse } from 'date-fns';
import { useGetShiftRosterQuery } from "@/api/services/attendanceManagement/shiftRoster/ShiftRoster";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import FormikMonthYearPicker from "@/components/forms/FormikMonthYearPicker";
import { StyledButton } from "@/components";
import useFetchCommonData from "@/hooks/useFetchCommonData";
import { off } from "rsuite/esm/DOMHelper";
import DataTable from "@/components/dataTableComponents/DataTable";
import { minWidth } from "@mui/system";
import CommonDataTable from "@/components/dataTableComponents/NewDatatablePagination";

const ShiftRoster = () => {

  const [empId, setEmpId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(moment().format("MM-YYYY"));

  const [dataTableKey, setDataTableKey] = useState(0);

  // pagination
   const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchText, setSearchText] = useState("");
const [loading, setLoading] = useState(false);


  const { data: fetchAllEmployees } = useFetchCommonData("EMPLOYEES");
  const employeeOptions =
    fetchAllEmployees &&
    fetchAllEmployees.map((item) => ({
      value: item.empId,
      label: item.employeeName,
    }));

  const { data: shiftRosterDetails = [], refetch: refetchShiftRoster, isLoading, isUninitialized } =
    useGetShiftRosterQuery(
      {
        month: selectedMonth,
        ...(empId != null ? { empId } : {}), // Only include empId if it's NOT null or undefined
        page: currentPage,
        size: pageSize,
        sortDirection: "DESC",
        search: searchText
      },
      { skip: !selectedMonth }
    );
  const rows = shiftRosterDetails?.data?.content || [];
  const totalRows = shiftRosterDetails?.data?.totalElements || 0;
  const pageCount = shiftRosterDetails?.data?.totalPages || Math.ceil(totalRows / pageSize);

  const flattenRowData = (row) => {
    return {
      empId: row.empId || "",
      employeeName: row.employeeName || "",
      shiftCount: row.shiftCount || 0,
      offCount: row.offCount || 0,
      ...Object.fromEntries(
        row.rosterDetails?.map(({ date, shift }) => [date, shift]) || []
      )
    };
  };

  const flattenedRows = rows.map(flattenRowData);

   const refetchData = async () => {
      setLoading(true);
      try {
        await refetchShiftRoster();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      if (!isUninitialized) {
        refetchData();
      }
    }, [selectedMonth, empId,currentPage, pageSize, searchText, refetchShiftRoster, isUninitialized]);


  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
  }, []);

  useEffect(() => {
      setCurrentPage(0); // Reset to page 0 when empId changes
    }, [empId]);
  
    // Reset pagination when month changes
    useEffect(() => {
      setCurrentPage(0);
    }, [selectedMonth]);

  // const filteredRows = rows;
  // const totalRows = filteredRows.length;

  const [dynamicDateColumns, setDynamicDateColumns] = useState([]);

  useEffect(() => {
    if (selectedMonth) {
      const parsedMonth = parse(selectedMonth, "MM-yyyy", new Date());
      const newStartDate = startOfMonth(parsedMonth);
      const newEndDate = endOfMonth(parsedMonth);

      const columns = eachDayOfInterval({ start: newStartDate, end: newEndDate }).map((date) => ({
        field: format(date, "yyyy-MM-dd"),
        headerName: format(date, "dd iiiiii"),
        minWidth: 20,
      }));
      setDynamicDateColumns(columns);
    }
  }, [selectedMonth]);


  const staticColumns1 = [
    { field: "empId", headerName: "Employee ID",  },
    { field: "employeeName", headerName: "Employee Name", },
    { field: "shiftCount", headerName: "WD", minWidth: 50, },
    { field: "offCount", headerName: "OFF", minWidth: 50, },

  ];


  const columns = useMemo(() => {
    return [...staticColumns1, ...dynamicDateColumns];
  }, [dynamicDateColumns, staticColumns1]);


  const [visibleColumns, setVisibleColumns] = useState([]);

  useEffect(() => {
    const newVisibleColumns = columns.map((column) => ({ ...column, visible: true }));

    // Only update state if there's a real change
    if (JSON.stringify(newVisibleColumns) !== JSON.stringify(visibleColumns)) {
      setVisibleColumns(newVisibleColumns);
    }
  }, [columns]); // Runs only when `columns` changes


  const handleToggleColumn = (field) => {
    setVisibleColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.field === field ? { ...column, visible: !column.visible } : column
      )
    );
  };

  const renderedColumns = visibleColumns.filter((column) => column.visible);


  useEffect(() => {
    setDataTableKey((prev) => prev + 1)
  }, [visibleColumns])

  // const employeeCols = staticColumns1.length;
  // const shiftCols = dynamicDateColumns.length;


  // 🔥 Correct dynamic header span calculation
  const employeeCols = visibleColumns.filter((col) =>
    ["empId"].includes(col.field)
  ).length;
  const shiftCols = visibleColumns.length - employeeCols;

  return (
    <CardContent title="Shift Roster">

      <Formik
        initialValues={{
          employee: "",
          startDate: selectedMonth ? moment(selectedMonth, "MM-YYYY").format("MMMM YYYY") : ""
        }}
        onSubmit={(values) => {
          setEmpId(values.employee || null);
          setSelectedMonth(values.startDate);
        }}
      >
        {({ setFieldValue, values, resetForm }) => (
          <Form>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormikSelectInput
                    name="employee"
                    label="Employee"
                    options={employeeOptions}
                    placeholder={`Select Employee`}
                    width="100%"
                    search={true}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFieldValue("employee", value);
                      setEmpId(value || null);
                    }}
                  />
                  <FormikMonthYearPicker
                    name="startDate"
                    label="Month Year"
                    pickerType="month"
                    placeholder="Select"
                    value={
                      values.startDate
                        ? moment(values.startDate, "MM-YYYY").format("MMMM YYYY")
                        : ""
                    }
                    onChange={(formattedDate) => {
                      const formatted = formattedDate
                        ? moment(formattedDate).format("MM-YYYY")
                        : "";
                      setFieldValue("startDate", formatted);
                      setSelectedMonth(formatted);
                    }}
                    maxDate={new Date()}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} container justifyContent="flex-end">
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                  <StyledButton
                    sx={{ p: (theme) => theme.spacing(1, 2), m: (theme) => theme.spacing(0, 1), borderRadius: (theme) => theme.shape.s, height: 40 }}
                    type="reset"
                    label="Reset"
                    variant="outlined"
                    color="error"
                    size="medium"
                    onClick={() => {
                      resetForm();
                      setSelectedMonth(moment().format("MM-YYYY"));
                      setEmpId(null)
                      setCurrentPage(0);
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>


      <CommonDataTable
        columns={renderedColumns}
        rows={flattenedRows}
        checkbox={false}
        search={false}
        loading={loading || isLoading}
        // onSearchChange={handleSearchChange}
        pageCount={pageCount}
        totalRecords={totalRows}
        onPageChange={handlePageChange}
        onPageSizeChange={handleRowsPerPageChange}
        currentPage={currentPage}
        currentPageSize={pageSize}
      />



    </CardContent>
  );
};

export default ShiftRoster;
